import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Input,
  Select,
  DatePicker,
  message,
  Tooltip,
  Badge,
  Card,
  Row,
  Col,
  Descriptions,
  Typography,
  Divider,
  Upload,
  Progress,
  Alert,
  Tabs,
  Switch,
  Dropdown,
  Menu,
  Form,
  Empty,
  Spin,
  Statistic,
  List,
  Avatar,
  QRCode,
} from 'antd';
import { apiService } from '../../services/api';
import type { ColumnsType } from 'antd/es/table';
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DownloadOutlined,
  QrcodeOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface DPPItem {
  key: string;
  dpp_id: string;
  // ── Product (ProductRead) ──
  product_name: string;
  product_legalName: string;
  product_displayName: string;
  product_description: string;
  product_disambiguatingDescription: string;
  product_alternateDescription: string;
  gtin: string;
  gtin8: string;
  gtin12: string;
  gtin13: string;
  gtin14: string;
  mpn: string;
  nsn: string;
  sku: string;
  asin: string;
  eclass: string;
  hsCode6: string;
  hsCode8: string;
  hsCode11: string;
  model: string;
  notesProductManager: string;
  sortOrder: string;
  customSortOrder: string;
  sameAs: string;
  sha256: string;
  cdmmId: string;
  assetId: string;
  globalAssetId: string;
  uri: string;
  irdi: string;
  url: string;
  releaseDate: string;
  datePublished: string;
  countryOfAssembly: string;
  countryOfLastProcessing: string;
  // ── Organisation (OrganisationRead) ──
  manufacturer_name: string;
  manufacturer_legalName: string;
  manufacturer_additionalName: string;
  manufacturer_displayName: string;
  manufacturer_taxId: string;
  manufacturer_location: string;
  manufacturer_globalLocationNumber: string;
  manufacturer_iso6523Code: string;
  manufacturer_isicV4: string;
  manufacturer_leiCode: string;
  manufacturer_cdmmId: string;
  manufacturer_assetId: string;
  manufacturer_globalAssetId: string;
  manufacturer_uri: string;
  manufacturer_sameAs: string;
  manufacturer_url: string;
  // PostalAddress
  manufacturer_streetAddress: string;
  manufacturer_postalCode: string;
  manufacturer_addressLocality: string;
  manufacturer_addressRegion: string;
  manufacturer_addressCountry: string;
  // ContactPoint
  manufacturer_telephone: string;
  manufacturer_email: string;
  manufacturer_faxNumber: string;
  manufacturer_contactType: string;
  // ── Brand (BrandRead) ──
  brand_name: string;
  brand_displayName: string;
  brand_slogan: string;
  brand_cdmmId: string;
  brand_url: string;
  // ── Category (CategoryRead) ──
  category: string;
  category_codeValue: string;
  category_level: string;
  categoryHierarchy: string;
  category_customCategoryHierarchy: string;
  category_floraGroup: string;
  category_faunaGroup: string;
  category_cdmmId: string;
  // ── ProductGroup (ProductGroupRead) ──
  productGroup_name: string;
  productGroup_cdmmId: string;
  productGroup_description: string;
  // ── ProductModel (ProductModelRead) ──
  productModel_name: string;
  productModel_cdmmId: string;
  productModel_description: string;
  // ── Person (PersonRead) ──
  person_givenName: string;
  person_additionalName: string;
  person_familyName: string;
  person_legalName: string;
  person_location: string;
  person_globalLocationNumber: string;
  person_iso6523Code: string;
  person_leiCode: string;
  // ── Certification (CertificationRead) ──
  certifications_count: number;
  certification_name: string;
  certification_about: string;
  certification_certificationIdentification: string;
  certification_certificationStatus: string;
  certification_hasMeasurement: string;
  certification_auditDate: string;
  certification_validFrom: string;
  certification_validThrough: string;
  // ── ProductPropertyValue (ProductPropertyValueRead) ──
  attributes_count: number;
  attribute_propertyName: string;
  attribute_valueText: string;
  attribute_valueWholeNumber: string;
  attribute_valueFloatingPointNumber: string;
  attribute_keyFeature: boolean;
  attribute_keyFilter: boolean;
  attribute_sortOrder: string;
  // ── Observation (ObservationRead) ──
  observations_count: number;
  observation_name: string;
  observation_variableMeasured: string;
  observation_measurementMethod: string;
  observation_measurementTechnique: string;
  observation_marginOfError: string;
  observation_observationDate: string;
  // ── Linking (LinkingRead) ──
  linkings_count: number;
  linking_templateName: string;
  linking_categoryHierarchy: string;
  linking_sortOrder: string;
  // ── LinkingType (LinkingTypeRead) ──
  linkingType_name: string;
  linkingType_forProduct: boolean;
  linkingType_forThing: boolean;
  linkingType_forMediaObject: boolean;
  // ── Property / Unit / Value ──
  properties_count: number;
  units_count: number;
  values_count: number;
  property_name: string;
  unit_name: string;
  unit_DSI: string;
  value_name: string;
  // ── Currency (CurrencyRead) ──
  currency_alphaCode3: string;
  currency_codeNumeric: string;
  currency_symbol: string;
  currency_letsCode: string;
  // ── Language / Dictionary ──
  language_name: string;
  language_codeAlpha2: string;
  language_codeAlpha3: string;
  language_lcid: string;
  dictionaries_count: number;
  dictionary_dataEntry: string;
  // ── Thing (ThingRead) ──
  things_count: number;
  thing_name: string;
  thing_cdmmId: string;
  thing_description: string;
  // ── Offer (OfferRead) ──
  offers_count: number;
  offer_price: string;
  offer_priceCurrency: string;
  offer_valueAddedTaxIncluded: boolean;
  offer_name: string;
  // ── MediaObject (MediaObjectRead) ──
  mediaObjects_count: number;
  mediaObject_name: string;
  mediaObject_contentUrl: string;
  mediaObject_encodingFormat: string;
  mediaObject_mediaType: string;
  mediaObject_fileName: string;
  mediaObject_size: string;
  // ── Status & Scores ──
  status: 'draft' | 'in_review' | 'validated' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
  completeness_score: number;
  compliance_score: number;
  validFrom: string;
  validThrough: string;
  expires: string;
}

const DppList: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DPPItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedDPP, setSelectedDPP] = useState<DPPItem | null>(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrUrl, setQrUrl] = useState<string>('');
  const [qrLoading, setQrLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: null as any,
    manufacturer: '',
  });

  // Mock data based on DPP schema – realistic automotive examples
  const mockData: DPPItem[] = [
    {
      key: '1',
      dpp_id: 'DPP-2024-001',
      // ── Product ──
      product_name: '48-Pin Hochstrom-Steckverbinder HV480',
      product_legalName: 'HV480-48P Hochstrom-Steckverbinder',
      product_displayName: 'HV480 48-Pin Connector',
      product_description: '48-poliger Hochstrom-Steckverbinder für 800V-Bordnetzarchitekturen in Elektrofahrzeugen, IP6K9K-geschützt, Temperaturbereich -40°C bis +150°C, Kontaktwiderstand <0.5mΩ',
      product_disambiguatingDescription: 'Automotive-Hochstromstecker für 800V-Plattformen, nicht für Industrieanwendungen',
      product_alternateDescription: '48-pin high-current connector for 800V EV architectures, IP6K9K rated',
      gtin: '4019238041234',
      gtin8: '',
      gtin12: '',
      gtin13: '4019238041234',
      gtin14: '',
      mpn: 'HV480-48P-R1',
      nsn: '',
      sku: 'TE-HV480-048-AU',
      asin: 'B0CQXR7K2M',
      eclass: '27-27-04-08',
      hsCode6: '854449',
      hsCode8: '85444900',
      hsCode11: '85444900000',
      model: 'HV480',
      notesProductManager: 'Freigabe für SOP Q3/2024, Nachfolger von HV320',
      sortOrder: '100',
      customSortOrder: 'TE-HV-001',
      sameAs: 'https://www.te.com/de/product/HV480-48P-R1.html',
      sha256: 'a3f2b8c1d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1',
      cdmmId: 'cdmm:te-connectivity:hv480-48p',
      assetId: 'urn:2024:te:hv480-48p-r1',
      globalAssetId: 'urn:2024:global:te:hv480-48p-r1',
      uri: 'https://dpp.te.com/product/hv480-48p-r1',
      irdi: '0173-1#01-AKJ234#001',
      url: 'https://www.te.com/de/product/HV480-48P-R1.html',
      releaseDate: '2024-08-15',
      datePublished: '2024-11-05T08:12:00Z',
      countryOfAssembly: 'DE',
      countryOfLastProcessing: 'DE',
      // ── Organisation ──
      manufacturer_name: 'TE Connectivity',
      manufacturer_legalName: 'TE Connectivity Germany GmbH',
      manufacturer_additionalName: 'TE Connectivity Ltd.',
      manufacturer_displayName: 'TE Connectivity Germany',
      manufacturer_taxId: 'DE811164789',
      manufacturer_location: 'Bensheim, Hessen, Deutschland',
      manufacturer_globalLocationNumber: '4019238000004',
      manufacturer_iso6523Code: '0088:4019238000004',
      manufacturer_isicV4: '2610',
      manufacturer_leiCode: '529900FGWB6FGIUQJV49',
      manufacturer_cdmmId: 'cdmm:te-connectivity:org',
      manufacturer_assetId: 'urn:org:te-connectivity',
      manufacturer_globalAssetId: 'urn:global:org:te-connectivity',
      manufacturer_uri: 'https://dpp.te.com/organisation/te-germany',
      manufacturer_sameAs: 'https://www.wikidata.org/wiki/Q1377654',
      manufacturer_url: 'https://www.te.com/de',
      manufacturer_streetAddress: 'Ampèrestraße 12-14',
      manufacturer_postalCode: '64625',
      manufacturer_addressLocality: 'Bensheim',
      manufacturer_addressRegion: 'Hessen',
      manufacturer_addressCountry: 'DE',
      manufacturer_telephone: '+49 6251 133-0',
      manufacturer_email: 'info.de@te.com',
      manufacturer_faxNumber: '+49 6251 133-1900',
      manufacturer_contactType: 'Vertrieb',
      // ── Brand ──
      brand_name: 'TE Connectivity',
      brand_displayName: 'TE Connectivity Automotive',
      brand_slogan: 'Every connection counts',
      brand_cdmmId: 'cdmm:te:brand',
      brand_url: 'https://www.te.com',
      // ── Category ──
      category: 'Steckverbinder > Hochstrom > Automotive HV',
      category_codeValue: '27-27-04-08',
      category_level: '4',
      categoryHierarchy: '27-27-04-08',
      category_customCategoryHierarchy: 'Automotive > Elektrik > HV-Steckverbinder',
      category_floraGroup: '',
      category_faunaGroup: '',
      category_cdmmId: 'cdmm:eclass:27-27-04-08',
      // ── ProductGroup ──
      productGroup_name: 'Hochstrom-Steckverbinder HV',
      productGroup_cdmmId: 'cdmm:te:pg-hv-connectors',
      productGroup_description: 'Produktgruppe für alle HV-Steckverbinder im Automotive-Bereich',
      // ── ProductModel ──
      productModel_name: 'HV480-R1',
      productModel_cdmmId: 'cdmm:te:pm-hv480-r1',
      productModel_description: 'Modellreihe HV480 Revision 1 – 48-polig',
      // ── Person ──
      person_givenName: 'Stefan',
      person_additionalName: 'M.',
      person_familyName: 'Berger',
      person_legalName: 'Dipl.-Ing. Stefan Berger',
      person_location: 'Bensheim, Hessen',
      person_globalLocationNumber: '',
      person_iso6523Code: '',
      person_leiCode: '',
      // ── Certification ──
      certifications_count: 4,
      certification_name: 'IATF 16949:2016',
      certification_about: 'Qualitätsmanagementsystem Automobilindustrie',
      certification_certificationIdentification: 'IATF-2024-TE-001',
      certification_certificationStatus: 'active',
      certification_hasMeasurement: 'Audit Score 96%',
      certification_auditDate: '2024-06-15',
      certification_validFrom: '2024-07-01',
      certification_validThrough: '2027-06-30',
      // ── ProductPropertyValue ──
      attributes_count: 18,
      attribute_propertyName: 'Nennstrom',
      attribute_valueText: '',
      attribute_valueWholeNumber: '200',
      attribute_valueFloatingPointNumber: '',
      attribute_keyFeature: true,
      attribute_keyFilter: true,
      attribute_sortOrder: '10',
      // ── Observation ──
      observations_count: 3,
      observation_name: 'Kontaktwiderstandsmessung',
      observation_variableMeasured: 'Kontaktwiderstand',
      observation_measurementMethod: 'Vierleitermessung nach IEC 60512-2',
      observation_measurementTechnique: 'Kelvin-Messung',
      observation_marginOfError: '±0.05mΩ',
      observation_observationDate: '2024-10-20',
      // ── Linking ──
      linkings_count: 3,
      linking_templateName: 'product-category-mediaobject',
      linking_categoryHierarchy: 'Automotive > Elektrik > HV',
      linking_sortOrder: '10',
      // ── LinkingType ──
      linkingType_name: 'Produktzuordnung',
      linkingType_forProduct: true,
      linkingType_forThing: false,
      linkingType_forMediaObject: true,
      // ── Property / Unit / Value ──
      properties_count: 12,
      units_count: 8,
      values_count: 18,
      property_name: 'Nennstrom',
      unit_name: 'Ampere',
      unit_DSI: 'A',
      value_name: '200',
      // ── Currency ──
      currency_alphaCode3: 'EUR',
      currency_codeNumeric: '978',
      currency_symbol: '€',
      currency_letsCode: '',
      // ── Language / Dictionary ──
      language_name: 'Deutsch',
      language_codeAlpha2: 'de',
      language_codeAlpha3: 'deu',
      language_lcid: '1031',
      dictionaries_count: 24,
      dictionary_dataEntry: 'Hochstrom-Steckverbinder',
      // ── Thing ──
      things_count: 2,
      thing_name: 'Gehäuse HV480',
      thing_cdmmId: 'cdmm:te:thing-gehaeuse-hv480',
      thing_description: 'Kunststoffgehäuse PA66-GF35 für HV480 Steckverbinder',
      // ── Offer ──
      offers_count: 1,
      offer_price: '48.90',
      offer_priceCurrency: 'EUR',
      offer_valueAddedTaxIncluded: false,
      offer_name: 'Listenpreis ab 100 Stk.',
      // ── MediaObject ──
      mediaObjects_count: 3,
      mediaObject_name: 'HV480 Produktbild',
      mediaObject_contentUrl: 'https://cdn.te.com/hv480/product-image.jpg',
      mediaObject_encodingFormat: 'image/jpeg',
      mediaObject_mediaType: 'image',
      mediaObject_fileName: 'hv480-product-image.jpg',
      mediaObject_size: '2.4 MB',
      // ── Status ──
      status: 'published',
      created_at: '2024-11-05T08:12:00Z',
      updated_at: '2025-01-20T11:45:00Z',
      completeness_score: 98,
      compliance_score: 96,
      validFrom: '2024-11-01',
      validThrough: '2027-10-31',
      expires: '2027-12-31',
    },
    {
      key: '2',
      dpp_id: 'DPP-2024-002',
      // ── Product ──
      product_name: 'Motorkabelbaum W213 Facelift',
      product_legalName: 'Kabelbaum Motorraum W213-FL 48V',
      product_displayName: 'Engine Harness W213 FL 48V',
      product_description: 'Hauptkabelbaum Motorraum für Mercedes-Benz E-Klasse W213 Facelift (ab MJ 2021), 48V-Mildhybrid-Variante, 342 Einzelleitungen, CAN-FD und LIN-Bus integriert, Flammschutzklasse UL94-V0',
      product_disambiguatingDescription: 'Nur für W213 Facelift 48V-Mildhybrid, nicht für Vorfacelift oder vollelektrische Varianten',
      product_alternateDescription: 'Main engine wiring harness for Mercedes-Benz E-Class W213 FL, 48V mild hybrid variant',
      gtin: '4005176893421',
      gtin8: '',
      gtin12: '',
      gtin13: '4005176893421',
      gtin14: '',
      mpn: 'A2135406803',
      nsn: '',
      sku: 'KB-W213-FL-48V',
      asin: '',
      eclass: '27-14-11-01',
      hsCode6: '854430',
      hsCode8: '85443000',
      hsCode11: '85443000000',
      model: 'W213-FL-48V',
      notesProductManager: 'OEM-Exklusivteil Mercedes-Benz, Änderungsstand E07',
      sortOrder: '200',
      customSortOrder: 'DRX-KB-002',
      sameAs: 'https://www.draexlmaier.com/produkte/kabelsaetze',
      sha256: 'b4c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c3',
      cdmmId: 'cdmm:draexlmaier:kb-w213-fl',
      assetId: 'urn:2024:draexlmaier:a2135406803',
      globalAssetId: 'urn:2024:global:draexlmaier:a2135406803',
      uri: 'https://dpp.draexlmaier.com/product/kb-w213-fl',
      irdi: '0173-1#01-AHK891#003',
      url: 'https://www.draexlmaier.com/produkte/kabelsaetze',
      releaseDate: '2024-03-01',
      datePublished: '2024-09-18T13:22:00Z',
      countryOfAssembly: 'RO',
      countryOfLastProcessing: 'DE',
      // ── Organisation ──
      manufacturer_name: 'DRÄXLMAIER Group',
      manufacturer_legalName: 'Dräxlmaier Group GmbH & Co. KG',
      manufacturer_additionalName: 'Fritz Dräxlmaier GmbH & Co. KG',
      manufacturer_displayName: 'DRÄXLMAIER Group',
      manufacturer_taxId: 'DE131103785',
      manufacturer_location: 'Vilsbiburg, Bayern, Deutschland',
      manufacturer_globalLocationNumber: '4005176000003',
      manufacturer_iso6523Code: '0088:4005176000003',
      manufacturer_isicV4: '2930',
      manufacturer_leiCode: '529900WJKDIQ3JHR5S14',
      manufacturer_cdmmId: 'cdmm:draexlmaier:org',
      manufacturer_assetId: 'urn:org:draexlmaier',
      manufacturer_globalAssetId: 'urn:global:org:draexlmaier',
      manufacturer_uri: 'https://dpp.draexlmaier.com/organisation/draexlmaier',
      manufacturer_sameAs: 'https://www.wikidata.org/wiki/Q1256199',
      manufacturer_url: 'https://www.draexlmaier.com',
      manufacturer_streetAddress: 'Landshuter Straße 100',
      manufacturer_postalCode: '84137',
      manufacturer_addressLocality: 'Vilsbiburg',
      manufacturer_addressRegion: 'Bayern',
      manufacturer_addressCountry: 'DE',
      manufacturer_telephone: '+49 8741 47-0',
      manufacturer_email: 'info@draexlmaier.com',
      manufacturer_faxNumber: '+49 8741 47-2100',
      manufacturer_contactType: 'Kundenservice',
      // ── Brand ──
      brand_name: 'DRÄXLMAIER',
      brand_displayName: 'DRÄXLMAIER Group',
      brand_slogan: 'Premium in jedem Detail',
      brand_cdmmId: 'cdmm:draexlmaier:brand',
      brand_url: 'https://www.draexlmaier.com',
      // ── Category ──
      category: 'Kabelbäume > Motorraum > PKW',
      category_codeValue: '27-14-11-01',
      category_level: '4',
      categoryHierarchy: '27-14-11-01',
      category_customCategoryHierarchy: 'Automotive > Bordnetz > Kabelbäume > Motorraum',
      category_floraGroup: '',
      category_faunaGroup: '',
      category_cdmmId: 'cdmm:eclass:27-14-11-01',
      // ── ProductGroup ──
      productGroup_name: 'Kabelbäume Motorraum PKW',
      productGroup_cdmmId: 'cdmm:draexlmaier:pg-kb-motor',
      productGroup_description: 'Alle Motorkabelbäume für PKW-Plattformen',
      // ── ProductModel ──
      productModel_name: 'W213-FL-48V',
      productModel_cdmmId: 'cdmm:draexlmaier:pm-w213-fl-48v',
      productModel_description: 'Modell W213 Facelift 48V Mildhybrid-Variante',
      // ── Person ──
      person_givenName: 'Claudia',
      person_additionalName: '',
      person_familyName: 'Meier',
      person_legalName: 'Dr.-Ing. Claudia Meier',
      person_location: 'Vilsbiburg, Bayern',
      person_globalLocationNumber: '',
      person_iso6523Code: '',
      person_leiCode: '',
      // ── Certification ──
      certifications_count: 5,
      certification_name: 'VDA 6.3:2023',
      certification_about: 'Prozessaudit Automobilindustrie',
      certification_certificationIdentification: 'VDA63-2024-DRX-001',
      certification_certificationStatus: 'active',
      certification_hasMeasurement: 'Audit Score 92%',
      certification_auditDate: '2024-04-22',
      certification_validFrom: '2024-05-01',
      certification_validThrough: '2027-04-30',
      // ── ProductPropertyValue ──
      attributes_count: 24,
      attribute_propertyName: 'Anzahl Einzelleitungen',
      attribute_valueText: '',
      attribute_valueWholeNumber: '342',
      attribute_valueFloatingPointNumber: '',
      attribute_keyFeature: true,
      attribute_keyFilter: false,
      attribute_sortOrder: '10',
      // ── Observation ──
      observations_count: 5,
      observation_name: 'Isolationswiderstandsprüfung',
      observation_variableMeasured: 'Isolationswiderstand',
      observation_measurementMethod: 'Hochspannungsprüfung nach LV 214',
      observation_measurementTechnique: 'Megger-Messung 1000V DC',
      observation_marginOfError: '±5%',
      observation_observationDate: '2024-08-10',
      // ── Linking ──
      linkings_count: 5,
      linking_templateName: 'product-category-organisation',
      linking_categoryHierarchy: 'Automotive > Bordnetz',
      linking_sortOrder: '20',
      // ── LinkingType ──
      linkingType_name: 'OEM-Zuordnung',
      linkingType_forProduct: true,
      linkingType_forThing: true,
      linkingType_forMediaObject: false,
      // ── Property / Unit / Value ──
      properties_count: 18,
      units_count: 11,
      values_count: 24,
      property_name: 'Leitungsanzahl',
      unit_name: 'Stück',
      unit_DSI: 'pcs',
      value_name: '342',
      // ── Currency ──
      currency_alphaCode3: 'EUR',
      currency_codeNumeric: '978',
      currency_symbol: '€',
      currency_letsCode: '',
      // ── Language / Dictionary ──
      language_name: 'Deutsch',
      language_codeAlpha2: 'de',
      language_codeAlpha3: 'deu',
      language_lcid: '1031',
      dictionaries_count: 36,
      dictionary_dataEntry: 'Motorkabelbaum',
      // ── Thing ──
      things_count: 4,
      thing_name: 'Leitungssatz Hauptstrang',
      thing_cdmmId: 'cdmm:draexlmaier:thing-hauptstrang',
      thing_description: 'Hauptstrang des Motorkabelbaums mit 186 Leitungen',
      // ── Offer ──
      offers_count: 2,
      offer_price: '1245.00',
      offer_priceCurrency: 'EUR',
      offer_valueAddedTaxIncluded: false,
      offer_name: 'OEM-Preis ab 1.000 Stk.',
      // ── MediaObject ──
      mediaObjects_count: 5,
      mediaObject_name: 'W213-FL Kabelbaum 3D-Modell',
      mediaObject_contentUrl: 'https://cdn.draexlmaier.com/w213-fl/3d-model.step',
      mediaObject_encodingFormat: 'model/step',
      mediaObject_mediaType: '3d-model',
      mediaObject_fileName: 'w213-fl-48v-harness.step',
      mediaObject_size: '48.7 MB',
      // ── Status ──
      status: 'validated',
      created_at: '2024-09-18T13:22:00Z',
      updated_at: '2025-02-03T09:10:00Z',
      completeness_score: 94,
      compliance_score: 92,
      validFrom: '2024-09-01',
      validThrough: '2028-08-31',
      expires: '2029-02-28',
    },
    {
      key: '3',
      dpp_id: 'DPP-2025-003',
      // ── Product ──
      product_name: 'Radar-Sensormodul 77 GHz LRR',
      product_legalName: 'Long Range Radar Sensor LRR5 77GHz',
      product_displayName: 'Bosch LRR5 77 GHz Radar',
      product_description: 'Long-Range-Radarsensor 77 GHz für ADAS Level 2+ und Level 3, Reichweite bis 250m, Öffnungswinkel ±60° azimut / ±15° elevation, ASIL-D nach ISO 26262, integrierter Signalprozessor',
      product_disambiguatingDescription: 'Nur für ADAS-Anwendungen, nicht für Industrieradar oder Schiffsnavigation',
      product_alternateDescription: 'Long-range radar sensor 77 GHz for ADAS L2+/L3, range up to 250m, ASIL-D compliant',
      gtin: '4047024973152',
      gtin8: '',
      gtin12: '',
      gtin13: '4047024973152',
      gtin14: '',
      mpn: '0203301110',
      nsn: '',
      sku: 'BOSCH-LRR5-77G',
      asin: 'B0D8FGH3N7',
      eclass: '27-20-90-14',
      hsCode6: '852610',
      hsCode8: '85261000',
      hsCode11: '85261000000',
      model: 'LRR5',
      notesProductManager: 'ASIL-D zertifiziert, Freigabe für Level 3 Autonomes Fahren',
      sortOrder: '300',
      customSortOrder: 'BOSCH-ADAS-003',
      sameAs: 'https://www.bosch-mobility.com/de/loesungen/fahrerassistenz/radarsensor/',
      sha256: 'c5d4e3f2a1b0c9d8e7f6a5b4c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b6c5d4',
      cdmmId: 'cdmm:bosch:lrr5-77g',
      assetId: 'urn:2025:bosch:0203301110',
      globalAssetId: 'urn:2025:global:bosch:0203301110',
      uri: 'https://dpp.bosch.com/product/lrr5-77g',
      irdi: '0173-1#01-ALD567#002',
      url: 'https://www.bosch-mobility.com/de/loesungen/fahrerassistenz/radarsensor/',
      releaseDate: '2025-01-15',
      datePublished: '2025-01-10T07:45:00Z',
      countryOfAssembly: 'DE',
      countryOfLastProcessing: 'DE',
      // ── Organisation ──
      manufacturer_name: 'Bosch',
      manufacturer_legalName: 'Robert Bosch GmbH',
      manufacturer_additionalName: 'Bosch Mobility Solutions',
      manufacturer_displayName: 'Robert Bosch GmbH',
      manufacturer_taxId: 'DE811128135',
      manufacturer_location: 'Gerlingen-Schillerhöhe, Baden-Württemberg, Deutschland',
      manufacturer_globalLocationNumber: '4047024000007',
      manufacturer_iso6523Code: '0088:4047024000007',
      manufacturer_isicV4: '2932',
      manufacturer_leiCode: '529900AQBND3S6YJLY83',
      manufacturer_cdmmId: 'cdmm:bosch:org',
      manufacturer_assetId: 'urn:org:bosch',
      manufacturer_globalAssetId: 'urn:global:org:bosch',
      manufacturer_uri: 'https://dpp.bosch.com/organisation/bosch',
      manufacturer_sameAs: 'https://www.wikidata.org/wiki/Q234021',
      manufacturer_url: 'https://www.bosch.de',
      manufacturer_streetAddress: 'Robert-Bosch-Platz 1',
      manufacturer_postalCode: '70839',
      manufacturer_addressLocality: 'Gerlingen',
      manufacturer_addressRegion: 'Baden-Württemberg',
      manufacturer_addressCountry: 'DE',
      manufacturer_telephone: '+49 711 811-0',
      manufacturer_email: 'kontakt@bosch.de',
      manufacturer_faxNumber: '+49 711 811-5180',
      manufacturer_contactType: 'Technischer Vertrieb',
      // ── Brand ──
      brand_name: 'Bosch Mobility',
      brand_displayName: 'Bosch Mobility Solutions',
      brand_slogan: 'Invented for life',
      brand_cdmmId: 'cdmm:bosch:brand-mobility',
      brand_url: 'https://www.bosch-mobility.com',
      // ── Category ──
      category: 'Sensorik > Radar > ADAS',
      category_codeValue: '27-20-90-14',
      category_level: '4',
      categoryHierarchy: '27-20-90-14',
      category_customCategoryHierarchy: 'Automotive > ADAS > Umfeldsensorik > Radar',
      category_floraGroup: '',
      category_faunaGroup: '',
      category_cdmmId: 'cdmm:eclass:27-20-90-14',
      // ── ProductGroup ──
      productGroup_name: 'Radarsensoren ADAS',
      productGroup_cdmmId: 'cdmm:bosch:pg-radar-adas',
      productGroup_description: 'Alle Radarsensoren für ADAS-Anwendungen',
      // ── ProductModel ──
      productModel_name: 'LRR5-77G',
      productModel_cdmmId: 'cdmm:bosch:pm-lrr5-77g',
      productModel_description: 'Long Range Radar Generation 5 – 77 GHz',
      // ── Person ──
      person_givenName: 'Markus',
      person_additionalName: 'A.',
      person_familyName: 'Hoffmann',
      person_legalName: 'Dr. Markus Hoffmann',
      person_location: 'Gerlingen-Schillerhöhe, BW',
      person_globalLocationNumber: '',
      person_iso6523Code: '',
      person_leiCode: '',
      // ── Certification ──
      certifications_count: 6,
      certification_name: 'ISO 26262 ASIL-D',
      certification_about: 'Funktionale Sicherheit Straßenfahrzeuge',
      certification_certificationIdentification: 'ISO26262-2025-BOSCH-LRR5',
      certification_certificationStatus: 'active',
      certification_hasMeasurement: 'ASIL-D Konformität bestätigt',
      certification_auditDate: '2024-12-05',
      certification_validFrom: '2025-01-01',
      certification_validThrough: '2028-12-31',
      // ── ProductPropertyValue ──
      attributes_count: 31,
      attribute_propertyName: 'Reichweite',
      attribute_valueText: '',
      attribute_valueWholeNumber: '250',
      attribute_valueFloatingPointNumber: '',
      attribute_keyFeature: true,
      attribute_keyFilter: true,
      attribute_sortOrder: '10',
      // ── Observation ──
      observations_count: 8,
      observation_name: 'Reichweitenmessung',
      observation_variableMeasured: 'Detektionsreichweite',
      observation_measurementMethod: 'Freifeldmessung nach ETSI EN 305 550',
      observation_measurementTechnique: 'Radarreflektormessung RCS 10 dBsm',
      observation_marginOfError: '±2m',
      observation_observationDate: '2025-01-08',
      // ── Linking ──
      linkings_count: 4,
      linking_templateName: 'product-thing-eligibleregion',
      linking_categoryHierarchy: 'Automotive > ADAS > Radar',
      linking_sortOrder: '30',
      // ── LinkingType ──
      linkingType_name: 'Sensorintegration',
      linkingType_forProduct: true,
      linkingType_forThing: true,
      linkingType_forMediaObject: true,
      // ── Property / Unit / Value ──
      properties_count: 22,
      units_count: 14,
      values_count: 31,
      property_name: 'Reichweite',
      unit_name: 'Meter',
      unit_DSI: 'm',
      value_name: '250',
      // ── Currency ──
      currency_alphaCode3: 'EUR',
      currency_codeNumeric: '978',
      currency_symbol: '€',
      currency_letsCode: '',
      // ── Language / Dictionary ──
      language_name: 'English',
      language_codeAlpha2: 'en',
      language_codeAlpha3: 'eng',
      language_lcid: '1033',
      dictionaries_count: 48,
      dictionary_dataEntry: 'Long Range Radar Sensor',
      // ── Thing ──
      things_count: 3,
      thing_name: 'Radom-Abdeckung LRR5',
      thing_cdmmId: 'cdmm:bosch:thing-radom-lrr5',
      thing_description: 'Radarabdeckung (Radom) aus PTFE-beschichtetem Polycarbonat',
      // ── Offer ──
      offers_count: 3,
      offer_price: '385.00',
      offer_priceCurrency: 'EUR',
      offer_valueAddedTaxIncluded: false,
      offer_name: 'Tier-1 Preis ab 10.000 Stk.',
      // ── MediaObject ──
      mediaObjects_count: 6,
      mediaObject_name: 'LRR5 Datenblatt',
      mediaObject_contentUrl: 'https://cdn.bosch.com/lrr5/datasheet-v2.pdf',
      mediaObject_encodingFormat: 'application/pdf',
      mediaObject_mediaType: 'document',
      mediaObject_fileName: 'lrr5-77g-datasheet-v2.pdf',
      mediaObject_size: '3.8 MB',
      // ── Status ──
      status: 'in_review',
      created_at: '2025-01-10T07:45:00Z',
      updated_at: '2025-02-17T16:30:00Z',
      completeness_score: 88,
      compliance_score: 85,
      validFrom: '2025-03-01',
      validThrough: '2029-02-28',
      expires: '2029-06-30',
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setData(mockData);
    } catch (error) {
      message.error('Fehler beim Laden der DPP-Daten');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      in_review: 'processing',
      validated: 'warning',
      published: 'success',
      archived: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      draft: 'Entwurf',
      in_review: 'In Prüfung',
      validated: 'Validiert',
      published: 'Veröffentlicht',
      archived: 'Archiviert',
    };
    return texts[status] || status;
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#52c41a';
    if (score >= 70) return '#faad14';
    return '#ff4d4f';
  };

  const handleView = (record: DPPItem) => {
    setSelectedDPP(record);
    setViewModalVisible(true);
  };

  const handleEdit = (record: DPPItem) => {
    navigate('/dpp-creation', { state: { editMode: true, dppData: record } });
  };

  const handleQRCode = async (record: DPPItem) => {
    setSelectedDPP(record);
    setQrModalVisible(true);
    setQrLoading(true);
    
    try {
      const response = await apiService.getQRCodeUrl(record.dpp_id);
      setQrUrl(response.qr_url);
    } catch (error) {
      console.error('Failed to fetch QR URL:', error);
      setQrUrl(`https://dpp.company.com/verify/${record.dpp_id}`); // Fallback
    } finally {
      setQrLoading(false);
    }
  };

  const handleExport = (record: DPPItem) => {
    message.info(`Exportiere DPP ${record.dpp_id} als PDF...`);
    // In real app, this would trigger download
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    const v = value.toLowerCase();
    const filtered = mockData.filter(item =>
      item.product_name.toLowerCase().includes(v) ||
      item.manufacturer_legalName.toLowerCase().includes(v) ||
      item.gtin.includes(value) ||
      item.mpn.toLowerCase().includes(v) ||
      item.sku.toLowerCase().includes(v) ||
      item.brand_name.toLowerCase().includes(v) ||
      item.eclass.toLowerCase().includes(v) ||
      item.category.toLowerCase().includes(v)
    );
    setData(filtered);
  };

  const handleStatusFilter = (value: string) => {
    setFilters(prev => ({ ...prev, status: value }));
    if (value) {
      const filtered = mockData.filter(item => item.status === value);
      setData(filtered);
    } else {
      setData(mockData);
    }
  };

  const columns: ColumnsType<DPPItem> = [
    {
      title: 'DPP-ID',
      dataIndex: 'dpp_id',
      key: 'dpp_id',
      fixed: 'left',
      width: 130,
    },
    {
      title: 'Product.name',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
      ellipsis: { showTitle: false },
      render: (text: string, record: DPPItem) => (
        <Tooltip placement="topLeft" title={`${text} (${record.model})`}>
          <div>
            <strong>{text}</strong>
            <br />
            <span style={{ fontSize: 11, color: '#888' }}>{record.brand_name}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Organisation.legalName',
      dataIndex: 'manufacturer_legalName',
      key: 'manufacturer_legalName',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'gtin',
      dataIndex: 'gtin',
      key: 'gtin',
      width: 140,
    },
    {
      title: 'mpn',
      dataIndex: 'mpn',
      key: 'mpn',
      width: 110,
    },
    {
      title: 'eclass',
      dataIndex: 'eclass',
      key: 'eclass',
      width: 120,
      render: (text: string) => text ? <Tag>{text}</Tag> : '-',
    },
    {
      title: 'category',
      dataIndex: 'category',
      key: 'category',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'completeness',
      dataIndex: 'completeness_score',
      key: 'completeness_score',
      width: 80,
      render: (score: number) => (
        <Badge
          count={`${score}%`}
          style={{ backgroundColor: getScoreColor(score) }}
        />
      ),
    },
    {
      title: 'Certification',
      dataIndex: 'certifications_count',
      key: 'certifications_count',
      width: 60,
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'attributes',
      dataIndex: 'attributes_count',
      key: 'attributes_count',
      width: 60,
      render: (count: number) => <Tag>{count}</Tag>,
    },
    {
      title: 'createdOn',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      fixed: 'right',
      width: 180,
      render: (_: any, record: DPPItem) => (
        <Space size="small">
          <Tooltip title="Anzeigen">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="QR-Code">
            <Button type="text" icon={<QrcodeOutlined />} onClick={() => handleQRCode(record)} />
          </Tooltip>
          <Tooltip title="Exportieren">
            <Button type="text" icon={<DownloadOutlined />} onClick={() => handleExport(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div>
      <Card
        title="Digital Product Passes"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadData}
              loading={loading}
            >
              Aktualisieren
            </Button>
            <Button
              type="primary"
              onClick={() => navigate('/dpp-creation')}
            >
              Neuen DPP erstellen
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Search
            placeholder="Produkt, Hersteller, GTIN, MPN, SKU, eCl@ss..."
            allowClear
            style={{ width: 350 }}
            onSearch={handleSearch}
            enterButton={<SearchOutlined />}
          />
          <Select
            placeholder="Status filtern"
            allowClear
            style={{ width: 150 }}
            onChange={handleStatusFilter}
          >
            <Option value="draft">Entwurf</Option>
            <Option value="in_review">In Prüfung</Option>
            <Option value="validated">Validiert</Option>
            <Option value="published">Veröffentlicht</Option>
            <Option value="archived">Archiviert</Option>
          </Select>
          <RangePicker placeholder={['Von', 'Bis']} />
        </Space>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={data}
          rowSelection={rowSelection}
          loading={loading}
          scroll={{ x: 800 }}
          size="small"
          pagination={{
            total: data.length,
            pageSize: 5,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} von ${total} Einträgen`,
            responsive: true,
          }}
        />
      </Card>

      {/* View Modal */}
      <Modal
        title={`DPP Details: ${selectedDPP?.product_name}`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Schließen
          </Button>,
          <Button key="edit" type="primary" onClick={() => selectedDPP && handleEdit(selectedDPP)}>
            Bearbeiten
          </Button>,
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        {selectedDPP && (
          <Tabs defaultActiveKey="product" items={[
            {
              key: 'product',
              label: 'Product',
              children: (
                <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                  <Descriptions.Item label="DPP-ID">{selectedDPP.dpp_id}</Descriptions.Item>
                  <Descriptions.Item label="Status"><Tag color={getStatusColor(selectedDPP.status)}>{getStatusText(selectedDPP.status)}</Tag></Descriptions.Item>
                  <Descriptions.Item label="name" span={2}>{selectedDPP.product_name}</Descriptions.Item>
                  <Descriptions.Item label="legalName">{selectedDPP.product_legalName}</Descriptions.Item>
                  <Descriptions.Item label="displayName">{selectedDPP.product_displayName}</Descriptions.Item>
                  <Descriptions.Item label="description" span={2}>{selectedDPP.product_description}</Descriptions.Item>
                  <Descriptions.Item label="disambiguatingDescription" span={2}>{selectedDPP.product_disambiguatingDescription}</Descriptions.Item>
                  <Descriptions.Item label="alternateDescription" span={2}>{selectedDPP.product_alternateDescription}</Descriptions.Item>
                  <Descriptions.Item label="gtin">{selectedDPP.gtin}</Descriptions.Item>
                  <Descriptions.Item label="gtin13">{selectedDPP.gtin13}</Descriptions.Item>
                  <Descriptions.Item label="gtin8">{selectedDPP.gtin8 || '–'}</Descriptions.Item>
                  <Descriptions.Item label="gtin12">{selectedDPP.gtin12 || '–'}</Descriptions.Item>
                  <Descriptions.Item label="gtin14">{selectedDPP.gtin14 || '–'}</Descriptions.Item>
                  <Descriptions.Item label="mpn">{selectedDPP.mpn}</Descriptions.Item>
                  <Descriptions.Item label="nsn">{selectedDPP.nsn || '–'}</Descriptions.Item>
                  <Descriptions.Item label="sku">{selectedDPP.sku}</Descriptions.Item>
                  <Descriptions.Item label="asin">{selectedDPP.asin || '–'}</Descriptions.Item>
                  <Descriptions.Item label="model">{selectedDPP.model}</Descriptions.Item>
                  <Descriptions.Item label="eclass">{selectedDPP.eclass}</Descriptions.Item>
                  <Descriptions.Item label="hsCode6">{selectedDPP.hsCode6}</Descriptions.Item>
                  <Descriptions.Item label="hsCode8">{selectedDPP.hsCode8}</Descriptions.Item>
                  <Descriptions.Item label="hsCode11">{selectedDPP.hsCode11}</Descriptions.Item>
                  <Descriptions.Item label="notesProductManager" span={2}>{selectedDPP.notesProductManager}</Descriptions.Item>
                  <Descriptions.Item label="sortOrder">{selectedDPP.sortOrder}</Descriptions.Item>
                  <Descriptions.Item label="customSortOrder">{selectedDPP.customSortOrder}</Descriptions.Item>
                  <Descriptions.Item label="cdmmId">{selectedDPP.cdmmId}</Descriptions.Item>
                  <Descriptions.Item label="assetId">{selectedDPP.assetId}</Descriptions.Item>
                  <Descriptions.Item label="globalAssetId" span={2}>{selectedDPP.globalAssetId}</Descriptions.Item>
                  <Descriptions.Item label="uri" span={2}>{selectedDPP.uri}</Descriptions.Item>
                  <Descriptions.Item label="irdi">{selectedDPP.irdi}</Descriptions.Item>
                  <Descriptions.Item label="url" span={2}>{selectedDPP.url}</Descriptions.Item>
                  <Descriptions.Item label="sameAs" span={2}>{selectedDPP.sameAs}</Descriptions.Item>
                  <Descriptions.Item label="sha256" span={2}>{selectedDPP.sha256}</Descriptions.Item>
                  <Descriptions.Item label="releaseDate">{selectedDPP.releaseDate}</Descriptions.Item>
                  <Descriptions.Item label="datePublished">{selectedDPP.datePublished ? dayjs(selectedDPP.datePublished).format('DD.MM.YYYY HH:mm') : '–'}</Descriptions.Item>
                  <Descriptions.Item label="countryOfAssembly">{selectedDPP.countryOfAssembly}</Descriptions.Item>
                  <Descriptions.Item label="countryOfLastProcessing">{selectedDPP.countryOfLastProcessing}</Descriptions.Item>
                  <Descriptions.Item label="validFrom">{selectedDPP.validFrom}</Descriptions.Item>
                  <Descriptions.Item label="validThrough">{selectedDPP.validThrough}</Descriptions.Item>
                  <Descriptions.Item label="expires">{selectedDPP.expires}</Descriptions.Item>
                  <Descriptions.Item label="completeness"><Badge count={`${selectedDPP.completeness_score}%`} style={{ backgroundColor: getScoreColor(selectedDPP.completeness_score) }} /></Descriptions.Item>
                  <Descriptions.Item label="compliance"><Badge count={`${selectedDPP.compliance_score}%`} style={{ backgroundColor: getScoreColor(selectedDPP.compliance_score) }} /></Descriptions.Item>
                  <Descriptions.Item label="createdOn">{dayjs(selectedDPP.created_at).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                  <Descriptions.Item label="modifiedOn">{dayjs(selectedDPP.updated_at).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'organisation',
              label: 'Organisation / Person',
              children: (
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="Organisation.name">{selectedDPP.manufacturer_name}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.legalName">{selectedDPP.manufacturer_legalName}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.additionalName">{selectedDPP.manufacturer_additionalName}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.displayName">{selectedDPP.manufacturer_displayName}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.taxId">{selectedDPP.manufacturer_taxId}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.location">{selectedDPP.manufacturer_location}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.globalLocationNumber">{selectedDPP.manufacturer_globalLocationNumber}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.iso6523Code">{selectedDPP.manufacturer_iso6523Code}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.isicV4">{selectedDPP.manufacturer_isicV4}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.leiCode">{selectedDPP.manufacturer_leiCode}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.cdmmId">{selectedDPP.manufacturer_cdmmId}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.assetId">{selectedDPP.manufacturer_assetId}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.globalAssetId" span={2}>{selectedDPP.manufacturer_globalAssetId}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.uri" span={2}>{selectedDPP.manufacturer_uri}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.sameAs" span={2}>{selectedDPP.manufacturer_sameAs}</Descriptions.Item>
                  <Descriptions.Item label="Organisation.url" span={2}>{selectedDPP.manufacturer_url}</Descriptions.Item>
                  <Descriptions.Item label="PostalAddress.streetAddress">{selectedDPP.manufacturer_streetAddress}</Descriptions.Item>
                  <Descriptions.Item label="PostalAddress.postalCode">{selectedDPP.manufacturer_postalCode}</Descriptions.Item>
                  <Descriptions.Item label="PostalAddress.addressLocality">{selectedDPP.manufacturer_addressLocality}</Descriptions.Item>
                  <Descriptions.Item label="PostalAddress.addressRegion">{selectedDPP.manufacturer_addressRegion}</Descriptions.Item>
                  <Descriptions.Item label="PostalAddress.addressCountry">{selectedDPP.manufacturer_addressCountry}</Descriptions.Item>
                  <Descriptions.Item label="ContactPoint.telephone">{selectedDPP.manufacturer_telephone}</Descriptions.Item>
                  <Descriptions.Item label="ContactPoint.email">{selectedDPP.manufacturer_email}</Descriptions.Item>
                  <Descriptions.Item label="ContactPoint.faxNumber">{selectedDPP.manufacturer_faxNumber}</Descriptions.Item>
                  <Descriptions.Item label="ContactPoint.contactType">{selectedDPP.manufacturer_contactType}</Descriptions.Item>
                  <Descriptions.Item label="Person.legalName" span={2}>{selectedDPP.person_legalName}</Descriptions.Item>
                  <Descriptions.Item label="Person.givenName">{selectedDPP.person_givenName}</Descriptions.Item>
                  <Descriptions.Item label="Person.additionalName">{selectedDPP.person_additionalName || '–'}</Descriptions.Item>
                  <Descriptions.Item label="Person.familyName">{selectedDPP.person_familyName}</Descriptions.Item>
                  <Descriptions.Item label="Person.location">{selectedDPP.person_location}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'classification',
              label: 'Brand / Category / Group / Model',
              children: (
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="Brand.name">{selectedDPP.brand_name}</Descriptions.Item>
                  <Descriptions.Item label="Brand.displayName">{selectedDPP.brand_displayName}</Descriptions.Item>
                  <Descriptions.Item label="Brand.slogan">{selectedDPP.brand_slogan}</Descriptions.Item>
                  <Descriptions.Item label="Brand.cdmmId">{selectedDPP.brand_cdmmId}</Descriptions.Item>
                  <Descriptions.Item label="Brand.url" span={2}>{selectedDPP.brand_url}</Descriptions.Item>
                  <Descriptions.Item label="Category.category" span={2}>{selectedDPP.category}</Descriptions.Item>
                  <Descriptions.Item label="Category.codeValue">{selectedDPP.category_codeValue}</Descriptions.Item>
                  <Descriptions.Item label="Category.level">{selectedDPP.category_level}</Descriptions.Item>
                  <Descriptions.Item label="Category.categoryHierarchy" span={2}>{selectedDPP.categoryHierarchy}</Descriptions.Item>
                  <Descriptions.Item label="Category.customCategoryHierarchy" span={2}>{selectedDPP.category_customCategoryHierarchy}</Descriptions.Item>
                  <Descriptions.Item label="Category.cdmmId">{selectedDPP.category_cdmmId}</Descriptions.Item>
                  <Descriptions.Item label="Category.floraGroup">{selectedDPP.category_floraGroup || '–'}</Descriptions.Item>
                  <Descriptions.Item label="Category.faunaGroup">{selectedDPP.category_faunaGroup || '–'}</Descriptions.Item>
                  <Descriptions.Item label="ProductGroup.name">{selectedDPP.productGroup_name}</Descriptions.Item>
                  <Descriptions.Item label="ProductGroup.cdmmId">{selectedDPP.productGroup_cdmmId}</Descriptions.Item>
                  <Descriptions.Item label="ProductGroup.description" span={2}>{selectedDPP.productGroup_description}</Descriptions.Item>
                  <Descriptions.Item label="ProductModel.name">{selectedDPP.productModel_name}</Descriptions.Item>
                  <Descriptions.Item label="ProductModel.cdmmId">{selectedDPP.productModel_cdmmId}</Descriptions.Item>
                  <Descriptions.Item label="ProductModel.description" span={2}>{selectedDPP.productModel_description}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'attributes',
              label: 'Attributes / Observations',
              children: (
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="ProductPropertyValue (count)"><Tag color="blue">{selectedDPP.attributes_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Attribute.propertyName">{selectedDPP.attribute_propertyName}</Descriptions.Item>
                  <Descriptions.Item label="Attribute.valueText">{selectedDPP.attribute_valueText || '–'}</Descriptions.Item>
                  <Descriptions.Item label="Attribute.valueWholeNumber">{selectedDPP.attribute_valueWholeNumber || '–'}</Descriptions.Item>
                  <Descriptions.Item label="Attribute.valueFloatingPointNumber">{selectedDPP.attribute_valueFloatingPointNumber || '–'}</Descriptions.Item>
                  <Descriptions.Item label="Attribute.keyFeature">{selectedDPP.attribute_keyFeature ? 'Ja' : 'Nein'}</Descriptions.Item>
                  <Descriptions.Item label="Attribute.keyFilter">{selectedDPP.attribute_keyFilter ? 'Ja' : 'Nein'}</Descriptions.Item>
                  <Descriptions.Item label="Attribute.sortOrder">{selectedDPP.attribute_sortOrder}</Descriptions.Item>
                  <Descriptions.Item label="Property (count)"><Tag>{selectedDPP.properties_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Property.name">{selectedDPP.property_name}</Descriptions.Item>
                  <Descriptions.Item label="Unit (count)"><Tag>{selectedDPP.units_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Unit.name">{selectedDPP.unit_name}</Descriptions.Item>
                  <Descriptions.Item label="Unit.DSI">{selectedDPP.unit_DSI}</Descriptions.Item>
                  <Descriptions.Item label="Value (count)"><Tag>{selectedDPP.values_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Value.name">{selectedDPP.value_name}</Descriptions.Item>
                  <Descriptions.Item label="Observation (count)"><Tag color="purple">{selectedDPP.observations_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Observation.name">{selectedDPP.observation_name}</Descriptions.Item>
                  <Descriptions.Item label="Observation.variableMeasured">{selectedDPP.observation_variableMeasured}</Descriptions.Item>
                  <Descriptions.Item label="Observation.measurementMethod" span={2}>{selectedDPP.observation_measurementMethod}</Descriptions.Item>
                  <Descriptions.Item label="Observation.measurementTechnique" span={2}>{selectedDPP.observation_measurementTechnique}</Descriptions.Item>
                  <Descriptions.Item label="Observation.marginOfError">{selectedDPP.observation_marginOfError}</Descriptions.Item>
                  <Descriptions.Item label="Observation.observationDate">{selectedDPP.observation_observationDate}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'certifications',
              label: 'Certifications / Linking',
              children: (
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="Certification (count)"><Tag color="blue">{selectedDPP.certifications_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Certification.name">{selectedDPP.certification_name}</Descriptions.Item>
                  <Descriptions.Item label="Certification.about" span={2}>{selectedDPP.certification_about}</Descriptions.Item>
                  <Descriptions.Item label="Certification.certificationIdentification">{selectedDPP.certification_certificationIdentification}</Descriptions.Item>
                  <Descriptions.Item label="Certification.certificationStatus"><Tag color={selectedDPP.certification_certificationStatus === 'active' ? 'green' : 'default'}>{selectedDPP.certification_certificationStatus}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Certification.hasMeasurement">{selectedDPP.certification_hasMeasurement}</Descriptions.Item>
                  <Descriptions.Item label="Certification.auditDate">{selectedDPP.certification_auditDate}</Descriptions.Item>
                  <Descriptions.Item label="Certification.validFrom">{selectedDPP.certification_validFrom}</Descriptions.Item>
                  <Descriptions.Item label="Certification.validThrough">{selectedDPP.certification_validThrough}</Descriptions.Item>
                  <Descriptions.Item label="Linking (count)"><Tag>{selectedDPP.linkings_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Linking.templateName">{selectedDPP.linking_templateName}</Descriptions.Item>
                  <Descriptions.Item label="Linking.categoryHierarchy">{selectedDPP.linking_categoryHierarchy}</Descriptions.Item>
                  <Descriptions.Item label="Linking.sortOrder">{selectedDPP.linking_sortOrder}</Descriptions.Item>
                  <Descriptions.Item label="LinkingType.name">{selectedDPP.linkingType_name}</Descriptions.Item>
                  <Descriptions.Item label="LinkingType.forProduct">{selectedDPP.linkingType_forProduct ? 'Ja' : 'Nein'}</Descriptions.Item>
                  <Descriptions.Item label="LinkingType.forThing">{selectedDPP.linkingType_forThing ? 'Ja' : 'Nein'}</Descriptions.Item>
                  <Descriptions.Item label="LinkingType.forMediaObject">{selectedDPP.linkingType_forMediaObject ? 'Ja' : 'Nein'}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'media',
              label: 'Media / Offer / Thing',
              children: (
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="MediaObject (count)"><Tag color="cyan">{selectedDPP.mediaObjects_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="MediaObject.name">{selectedDPP.mediaObject_name}</Descriptions.Item>
                  <Descriptions.Item label="MediaObject.contentUrl" span={2}>{selectedDPP.mediaObject_contentUrl}</Descriptions.Item>
                  <Descriptions.Item label="MediaObject.encodingFormat">{selectedDPP.mediaObject_encodingFormat}</Descriptions.Item>
                  <Descriptions.Item label="MediaObject.mediaType">{selectedDPP.mediaObject_mediaType}</Descriptions.Item>
                  <Descriptions.Item label="MediaObject.fileName">{selectedDPP.mediaObject_fileName}</Descriptions.Item>
                  <Descriptions.Item label="MediaObject.size">{selectedDPP.mediaObject_size}</Descriptions.Item>
                  <Descriptions.Item label="Offer (count)"><Tag color="gold">{selectedDPP.offers_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Offer.name">{selectedDPP.offer_name}</Descriptions.Item>
                  <Descriptions.Item label="Offer.price">{selectedDPP.offer_price} {selectedDPP.offer_priceCurrency}</Descriptions.Item>
                  <Descriptions.Item label="Offer.valueAddedTaxIncluded">{selectedDPP.offer_valueAddedTaxIncluded ? 'Ja' : 'Nein'}</Descriptions.Item>
                  <Descriptions.Item label="Currency.alphaCode3">{selectedDPP.currency_alphaCode3}</Descriptions.Item>
                  <Descriptions.Item label="Currency.codeNumeric">{selectedDPP.currency_codeNumeric}</Descriptions.Item>
                  <Descriptions.Item label="Currency.symbol">{selectedDPP.currency_symbol}</Descriptions.Item>
                  <Descriptions.Item label="Currency.letsCode">{selectedDPP.currency_letsCode || '–'}</Descriptions.Item>
                  <Descriptions.Item label="Thing (count)"><Tag color="orange">{selectedDPP.things_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Thing.name">{selectedDPP.thing_name}</Descriptions.Item>
                  <Descriptions.Item label="Thing.cdmmId">{selectedDPP.thing_cdmmId}</Descriptions.Item>
                  <Descriptions.Item label="Thing.description" span={2}>{selectedDPP.thing_description}</Descriptions.Item>
                  <Descriptions.Item label="Language.name">{selectedDPP.language_name}</Descriptions.Item>
                  <Descriptions.Item label="Language.codeAlpha2">{selectedDPP.language_codeAlpha2}</Descriptions.Item>
                  <Descriptions.Item label="Language.codeAlpha3">{selectedDPP.language_codeAlpha3}</Descriptions.Item>
                  <Descriptions.Item label="Language.lcid">{selectedDPP.language_lcid}</Descriptions.Item>
                  <Descriptions.Item label="Dictionary (count)"><Tag>{selectedDPP.dictionaries_count}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Dictionary.dataEntry">{selectedDPP.dictionary_dataEntry}</Descriptions.Item>
                </Descriptions>
              ),
            },
          ]} />
        )}
      </Modal>

      {/* QR Code Modal */}
      <Modal
        title={`QR-Code: ${selectedDPP?.product_name}`}
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setQrModalVisible(false)}>
            Schließen
          </Button>,
          <Button 
            key="download-qr" 
            type="primary"
            onClick={() => selectedDPP && apiService.downloadAASQRCode(selectedDPP.dpp_id)}
          >
            DPP QR Code Download
          </Button>,
        ]}
        width={400}
      >
        {selectedDPP && (
          <div style={{ textAlign: 'center' }}>
            {qrLoading ? (
              <Spin size="large" />
            ) : (
              <QRCode
                value={qrUrl}
                size={256}
              />
            )}
            <div style={{ marginTop: 16 }}>
              <p><strong>DPP-ID:</strong> {selectedDPP.dpp_id}</p>
              <p><strong>Produkt:</strong> {selectedDPP.product_name}</p>
              <p><strong>QR-Typ:</strong> IEC CDD & AAS Suite kompatibel</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DppList;
